import { fg } from '@confluence/feature-gating';
import { useIsCurrentPageLive } from '@confluence/live-pages-utils/entry-points/useIsCurrentPageLive.ts';
import { useContentType } from '@confluence/page-context';
import { useRouteName } from '@confluence/route-manager/entry-points/RouteState.ts';
import { CONTENT_HISTORY } from '@confluence/named-routes/entry-points/routes.ts';
import { isSpaceOverview } from '@confluence/named-routes/entry-points/routes-utils.ts';
import { useIsAIFCOnbardingEligible } from '@confluence/onboarding-ai-first-create/entry-points/useIsAIFCOnbardingEligible.ts';

import {
	useGetModernizeChangeboardingOnboardingState,
	useSetModernizeChangeboardingOnboardingState,
	STEPS,
} from '../../hooks/useModernizeChangeboardingOnboardingState.ts';
import type { SegmentHook } from '../../hooks/useCombinedSegments.tsx';

import { Spotlights } from './Spotlights.tsx';

export const useContentWrapperSegment: SegmentHook = () => {
	const {
		loading: dismissedContentWrapperTourLoading,
		error: dismissedContentWrapperTourError,
		hasDismissedContentWrapperTour,
	} = useGetModernizeChangeboardingOnboardingState();
	const {
		loading: isAIFCOnboardingEligibleLoading,
		error: isAIFCOnboardingEligibleError,
		isAIFCOnbardingEligible,
	} = useIsAIFCOnbardingEligible();
	const { completeContentWrapperTour } = useSetModernizeChangeboardingOnboardingState();

	const isLiveDoc = useIsCurrentPageLive();
	const [contentType] = useContentType();
	const isLiveDocOrClassicPage = isLiveDoc || contentType === 'page';
	const isVersionHistoryPage = useRouteName({
		selector: (routeName: string | undefined) => routeName === CONTENT_HISTORY.name,
	});
	const error = dismissedContentWrapperTourError || isAIFCOnboardingEligibleError;
	const isSpaceOverviewPage = isSpaceOverview();
	const shouldShowContentWrapperTour =
		!isAIFCOnbardingEligible &&
		!hasDismissedContentWrapperTour &&
		isLiveDocOrClassicPage &&
		!isVersionHistoryPage &&
		!isSpaceOverviewPage &&
		fg('confluence_frontend_content_wrapper');

	if (dismissedContentWrapperTourLoading || isAIFCOnboardingEligibleLoading) {
		return {
			steps: [],
			onComplete: () => {},
			loading: true,
		};
	}
	if (error) {
		return {
			steps: [],
			onComplete: () => {},
			error,
		};
	}
	if (!shouldShowContentWrapperTour) {
		return {
			steps: [],
			onComplete: () => {},
		};
	}

	return {
		stepsToComplete: [STEPS.CONTENT_WRAPPER],
		steps: [Spotlights.ObjectSidebarSpotlight, Spotlights.LastUpdatedSpotlight],
		onComplete: completeContentWrapperTour,
	};
};
